/**
 * Prépare une photo avant de l'envoyer.
 *
 * L'API n'accepte que le JPEG et le PNG, sous deux mégaoctets. Un iPhone envoie du HEIC de
 * quatre mégaoctets : les deux conditions échouent à la fois, et le membre reçoit un refus
 * sans comprendre ce qu'on attend de lui.
 *
 * Le navigateur sait décoder ce que le système sait lire — sur iOS, `createImageBitmap`
 * ouvre un HEIC sans difficulté, puisque le codec est dans l'appareil. On le réencode alors
 * en JPEG par un canvas, réduit à une taille raisonnable. Un seul geste règle le format et
 * le poids, et l'envoi devient bien plus rapide en 4G.
 *
 * Ce module ne remplace pas le nettoyage des métadonnées côté serveur. Le réencodage efface
 * l'EXIF au passage, mais c'est un effet de bord : le serveur est le seul bout de la chaîne
 * que nous contrôlons, et un client peut toujours poster ce qu'il veut.
 */

/** Au-delà, on réduit. Ni une vignette de bouteille ni un avatar n'ont besoin de plus. */
export const MAX_DIMENSION = 1600;

/** La même borne que l'API — la dépasser ne servirait qu'à se faire refuser. */
export const MAX_BYTES = 2 * 1024 * 1024;

/** Les qualités successives, du plus fidèle au plus léger. Bornées : pas de boucle infinie. */
const QUALITY_STEPS = [0.82, 0.7, 0.6] as const;

const PASSTHROUGH_TYPES = new Set(['image/jpeg', 'image/png']);

/**
 * Les dimensions visées, en conservant les proportions.
 *
 * On ne grandit jamais une image : agrandir une photo de 400 px ne lui ajoute aucun détail,
 * cela ne fait que gonfler le fichier.
 */
export function targetDimensions(
  width: number,
  height: number,
  max = MAX_DIMENSION,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= max || longest === 0) return { width, height };

  const ratio = max / longest;
  // `round` et non `floor` : sur une image très allongée, tronquer ferait dériver le rapport
  // d'aspect d'un pixel visible.
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

/**
 * Faut-il retoucher ce fichier ?
 *
 * Non si c'est déjà un JPEG ou un PNG, sous la limite de poids et sous la taille maximale :
 * réencoder alors ne ferait que dégrader une image qui passait déjà. Le cas est fréquent —
 * une capture d'écran, une photo déjà réduite.
 */
export function needsReencoding(
  type: string,
  bytes: number,
  width: number,
  height: number,
): boolean {
  if (!PASSTHROUGH_TYPES.has(type)) return true;
  if (bytes > MAX_BYTES) return true;
  return Math.max(width, height) > MAX_DIMENSION;
}

/**
 * Décode, réduit et réencode si nécessaire.
 *
 * En cas d'échec du décodage — un format qu'aucun codec local ne connaît — on renvoie le
 * fichier d'origine. L'API le refusera avec son propre message, ce qui vaut mieux qu'un
 * échec silencieux dont personne ne saurait la cause.
 */
export async function preparePhoto(file: File): Promise<File> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    if (!needsReencoding(file.type, file.size, bitmap.width, bitmap.height)) return file;

    const { width, height } = targetDimensions(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);

    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, quality);
      if (!blob) return file;
      // Le dernier palier est retenu même s'il dépasse encore : l'API dira alors non, avec
      // un message clair, plutôt que de nous voir boucler.
      if (blob.size <= MAX_BYTES || quality === QUALITY_STEPS[QUALITY_STEPS.length - 1]) {
        return new File([blob], renameToJpeg(file.name), {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });
      }
    }
    return file;
  } finally {
    // Sans cela, la mémoire du bitmap décodé reste retenue jusqu'au ramasse-miettes — sur
    // un téléphone, après trois photos, cela se sent.
    bitmap.close();
  }
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/** « bouteille.heic » devient « bouteille.jpg » : le nom ne doit pas mentir sur le contenu. */
function renameToJpeg(name: string): string {
  const base = name.replace(/\.[^.]+$/, '');
  return `${base || 'photo'}.jpg`;
}
